import React, { useState, useEffect } from 'react';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { Cpu, Monitor, CalendarDays, ArrowLeftRight } from 'lucide-react';
import HardwareSection from '../components/HardwareSection';
import SoftwareSection from '../components/SoftwareSection';
import ResourceCalendar from '../components/ResourceCalendar';
import BorrowingSection from '../components/BorrowingSection';
import { DeleteBookingModal } from '../components/modals/DeleteBookingModal';
import { VenueBookingModal } from '../components/modals/VenueBookingModal';
import { toast } from 'react-hot-toast';
import { resourceService } from '..';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

const TABS = [
  { id: 'hardware',  label: 'Hardware',      icon: Cpu },
  { id: 'software',  label: 'Software',      icon: Monitor },
  { id: 'venues',    label: 'Venue Booking', icon: CalendarDays },
  { id: 'borrowing', label: 'Borrowing',     icon: ArrowLeftRight },
];

const Resources = () => {
  const { user } = useApiAuth();
  const [activeTab, setActiveTab]             = useState('venues');
  const [bookings, setBookings]               = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot]     = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen]   = useState(false);
  const [bookingToDelete, setBookingToDelete]       = useState(null);
  const [selectedVenue]                       = useState('D24');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const start = new Date();
      start.setDate(1);
      const end = new Date();
      end.setMonth(end.getMonth() + 1, 0);
      const data = await resourceService.getBookings(start, end);
      setBookings(data);
    } catch {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleBookVenue = async (bookingData) => {
    try {
      setLoading(true);
      if (bookingData.action === 'delete') {
        await resourceService.removeBooking(bookingData.id);
        toast.success('Booking cancelled');
      } else {
        await resourceService.addBooking({ ...bookingData, userId: user.id });
        toast.success('Venue booked successfully');
      }
      await loadData();
      setIsBookingModalOpen(false);
    } catch (err) {
      toast.error(err.message || 'Failed to process booking');
    } finally {
      setLoading(false);
    }
  };

  const handleTimeSlotSelect = (slotData) => {
    if (slotData.action === 'delete') {
      setBookingToDelete(slotData);
      setIsDeleteModalOpen(true);
      return;
    }
    setSelectedTimeSlot(slotData);
    setIsBookingModalOpen(true);
  };

  const handleDeleteBooking = async () => {
    try {
      await resourceService.removeBooking(bookingToDelete.id);
      await loadData();
      toast.success('Booking cancelled');
      setIsDeleteModalOpen(false);
      setBookingToDelete(null);
    } catch {
      toast.error('Failed to cancel booking');
    }
  };

  return (
    <div
      className="min-h-screen px-4 py-6 sm:px-6 lg:px-8"
    >
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <div>
          <p
            className="text-xs font-semibold uppercase tracking-[0.18em]"
            style={{ color: BRAND_COLORS.primary }}
          >
            Shared Assets
          </p>
          <h1
            className="mt-1 text-2xl font-bold sm:text-3xl"
            style={{ color: NEUTRAL_COLORS.black }}
          >
            Resource Management
          </h1>
          <p className="mt-1 text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
            Hardware, software, venues and borrowing in one place
          </p>
        </div>

        {/* Tab panel */}
        <div
          className="rounded-2xl border overflow-hidden shadow-md"
          style={{
            backgroundColor: SYSTEM_COLORS.shellSurface,
            borderColor: SYSTEM_COLORS.borderSoft,
          }}
        >
          {/* Tab bar */}
          <div
            className="flex overflow-x-auto border-b"
            style={{ borderColor: SYSTEM_COLORS.borderSoft }}
          >
            {TABS.map(({ id, label, icon: Icon }) => {
              const active = activeTab === id;
              return (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className="flex items-center gap-2 px-5 py-3.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors"
                  style={{
                    borderBottomColor: active ? BRAND_COLORS.primary : 'transparent',
                    color: active ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted,
                    backgroundColor: active ? SYSTEM_COLORS.shellSurfaceMuted : 'transparent',
                  }}
                >
                  <Icon size={16} />
                  {label}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div className="p-5 sm:p-6">
            {loading && activeTab === 'venues' ? (
              <div className="flex items-center justify-center h-40">
                <div
                  className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
                  style={{ borderColor: BRAND_COLORS.primary, borderTopColor: 'transparent' }}
                />
              </div>
            ) : (
              <>
                {activeTab === 'venues'    && (
                  <ResourceCalendar
                    bookings={bookings.filter(b => b.venue === selectedVenue)}
                    onSelectTimeSlot={handleTimeSlotSelect}
                    selectedVenue={selectedVenue}
                  />
                )}
                {activeTab === 'hardware'  && <HardwareSection />}
                {activeTab === 'software'  && <SoftwareSection />}
                {activeTab === 'borrowing' && <BorrowingSection />}
              </>
            )}
          </div>
        </div>
      </div>

      <VenueBookingModal
        isOpen={isBookingModalOpen}
        onClose={() => { setIsBookingModalOpen(false); setSelectedTimeSlot(null); }}
        onSave={handleBookVenue}
        selectedTimeSlot={selectedTimeSlot}
        existingBookings={bookings}
      />

      <DeleteBookingModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setBookingToDelete(null); }}
        onConfirm={handleDeleteBooking}
      />
    </div>
  );
};

export default Resources;
